import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Body } from "../../../components/main-body/body";
import useNotification from "../../../components/notification/notification";
import ScenarioEditorWrapper from "../scenario-management/components/scenario-editor";
import UserActivityStatus from "../../../components/user-activity-status/user-activity-status";
import ChangeLogsModalComponent from "../../../components/modals/change-logs/change-logs-modal";
import ApiEnrollmentScenarioManagement from "../../../api/enrollment-scenario-management";
import ApiScenariosManagement from "../../../api/scenarios-management";
import debounce from "../../../common/utils/debounce";
import "./styles.css";

// Reserved-alias nodes that must not be deletable in the editor.
const PROTECTED_ALIASES = ["email", "cohort"];

// The editor calls every fetcher (Promise.all) on init and maps over a shape
// inside `data`; stubs must return the right shape. The restricted enrollment
// palette has no facts / articles / scenarios / tasks / image nodes.
const emptyList = () => Promise.resolve({ data: [] });
const emptyDynamic = () => Promise.resolve({ data: { ids: [], tags: [], categories: [] } });
const emptyFilteredDynamic = () =>
  Promise.resolve({ data: { ids: [], appliedFilters: { tags: [], categories: [] } } });
const emptyRecord = () => Promise.resolve({ data: {} });

const EnrollmentScenario = () => {
  const { openNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState(null);
  const [version, setVersion] = useState(null);
  const [changeLogs, setChangeLogs] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  // True while an edit is being autosaved and the change-log count re-fetched —
  // drives the "View changes" spinner and hides the (stale) count meanwhile.
  const [changesLoading, setChangesLoading] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [previewActive, setPreviewActive] = useState(false);
  const [previewGraph, setPreviewGraph] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ApiEnrollmentScenarioManagement.getEnrollmentScenario();
      setScenario(r.data.enrollmentScenario);
      setVersion(r.data.enrollmentScenario?.EnrollmentScenarioVersion || null);
      setChangeLogs(r.data.changeLogs || []);
      setDirty(false);
      setChangesLoading(false);
      setPreviewActive(false);
      setPreviewGraph(null);
    } catch (e) {
      openNotification({ type: "error", message: <p>Failed to load the subject invitation.</p> });
    } finally {
      setLoading(false);
    }
  }, [openNotification]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const versionIdRef = useRef(null);
  versionIdRef.current = version?.id || null;

  // Holds the most recent editor state that the debounced autosave hasn't
  // necessarily flushed yet — used to force-save before activate/discard.
  const latestDataRef = useRef(null);

  const persist = useCallback((data) => {
    const versionId = versionIdRef.current;
    if (!versionId) return Promise.resolve();
    return ApiEnrollmentScenarioManagement.updateScenarioVersion(versionId, { data });
  }, []);

  // Re-fetch the change-log count without reloading the whole page (so the editor
  // isn't remounted). The trail rows are written by the editor on its own
  // debounce, so retry a few times until they land. Mirrors the scenarios page.
  const refreshChangeLogs = useCallback(async ({ attempts = 5, delay = 400 } = {}) => {
    for (let i = 0; i < attempts; i++) {
      if (i > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      try {
        const r = await ApiEnrollmentScenarioManagement.getEnrollmentScenario();
        const logs = r.data.changeLogs || [];
        setChangeLogs(logs);
        setScenario((prev) =>
          prev ? { ...prev, status: r.data.enrollmentScenario?.status ?? prev.status } : prev
        );
        if (logs.length > 0) return logs;
      } catch (e) {
        // keep retrying / keep existing logs
      }
    }
    return null;
  }, []);

  const debouncedPersist = useMemo(
    () =>
      debounce((data) => {
        persist(data)
          .then(() => refreshChangeLogs())
          .catch(() => {
            openNotification({ type: "error", message: <p>Failed to save changes.</p> });
          })
          .finally(() => setChangesLoading(false));
      }, 800),
    [persist, refreshChangeLogs, openNotification]
  );

  const handleEditorChange = useCallback(
    (changedScenario) => {
      latestDataRef.current = changedScenario;
      setDirty(true);
      setChangesLoading(true);
      debouncedPersist(changedScenario);
    },
    [debouncedPersist]
  );

  const handleActivate = async () => {
    if (!version?.id) return;
    setBusy(true);
    try {
      // Flush any pending debounced autosave BEFORE activating. Otherwise a quick
      // Save click races the 800ms debounce: activate would publish the previously
      // persisted (stale) data and the last edit would be lost, or the backend
      // would reject it as "IsNotPending".
      debouncedPersist.cancel();
      setChangesLoading(false);
      if (latestDataRef.current != null) {
        await persist(latestDataRef.current);
        latestDataRef.current = null;
      }
      await ApiEnrollmentScenarioManagement.activateScenario(version.id);
      openNotification({ type: "success", message: <p>Changes saved.</p> });
      await load();
    } catch (e) {
      const code = e?.response?.data?.error?.code;
      openNotification({
        type: "error",
        message: <p>{code === "IsNotPending" ? "Nothing to save." : "Failed to save changes."}</p>,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = () => {
    if (!version?.id) return;
    // Drop any pending autosave so it can't resurrect the discarded edits.
    debouncedPersist.cancel();
    setChangesLoading(false);
    latestDataRef.current = null;
    setBusy(true);
    ApiEnrollmentScenarioManagement.discardScenarioChanges(version.id)
      .then(() => {
        openNotification({ type: "success", message: <p>Changes discarded.</p> });
        return load();
      })
      .catch(() => {
        openNotification({ type: "error", message: <p>Failed to discard changes.</p> });
      })
      .finally(() => setBusy(false));
  };

  const openLogs = async () => {
    // Refresh logs so the modal reflects the latest edits.
    try {
      const r = await ApiEnrollmentScenarioManagement.getEnrollmentScenario();
      setChangeLogs(r.data.changeLogs || []);
    } catch (e) {
      // keep existing logs on failure
    }
    setLogsOpen(true);
  };

  const handlePreviewActive = async () => {
    try {
      const r = await ApiEnrollmentScenarioManagement.getEnrollmentScenarioForm();
      setPreviewGraph(r.data?.enrollmentScenario?.data || {});
      setPreviewActive(true);
      setLogsOpen(false);
    } catch (e) {
      openNotification({ type: "error", message: <p>Failed to load the active version.</p> });
    }
  };

  const getProfileManagement = useCallback(
    () => async (nodeProps) => {
      const alias = nodeProps?.data?.alias ?? nodeProps?.customNodeData?.data?.alias;
      if (alias && PROTECTED_ALIASES.includes(alias)) {
        return { data: [{ id: nodeProps?.id }] };
      }
      return { data: [] };
    },
    []
  );

  const fetchers = useMemo(
    () => ({
      getAlternativeMessages: ApiScenariosManagement.getAlternativeMessages,
      getAllMessages: ApiScenariosManagement.getAllMessages,
      getOptionsByMessage: ApiScenariosManagement.getOptionsByMessage,
      getAllFitFacts: emptyDynamic,
      getAllArticles: emptyDynamic,
      getFilteredFitFacts: emptyFilteredDynamic,
      getFilteredArticles: emptyFilteredDynamic,
      getAllScenario: emptyList,
      getTaskManagements: emptyList,
      getProfileManagement: getProfileManagement(),
      fetchImageUrlById: emptyRecord,
      fetchImageContentById: emptyRecord,
      uploadImage: emptyRecord,
    }),
    [getProfileManagement]
  );

  const showChangesButtons = !previewActive && (dirty || version?.status === "pending" || scenario?.status === "pending");

  // Status + action buttons live in the page header (so they sit in the title
  // row on wide screens and stack below it on narrow ones — handled by the
  // shared Header's responsive layout).
  const headerConfig = (() => {
    const base = { title: "Subject Invitation" };
    if (loading || !version) return base;
    base.period = (
      <span className="enrollment-scenario__status">
        <UserActivityStatus userStatus={scenario?.status || "active"} fontSize={14} />
      </span>
    );
    if (previewActive) {
      base.apply = [
        { key: "return", label: "Return to draft", type: "primary", onClick: () => setPreviewActive(false) },
      ];
    } else if (showChangesButtons) {
      base.apply = [
        { key: "discard", label: "Discard changes", type: "default", onClick: handleDiscard, disabled: busy || changesLoading, loading: busy },
        { key: "view", label: `View changes${!changesLoading && changeLogs.length ? ` (${changeLogs.length})` : ""}`, type: "default", onClick: openLogs, disabled: busy || changesLoading, loading: busy || changesLoading },
        { key: "save", label: "Save", type: "primary", onClick: handleActivate, disabled: busy || changesLoading, loading: busy },
      ];
    }
    return base;
  })();

  return (
    <Body header={headerConfig}>
      {loading ? (
        <div className="enrollment-scenario__loading">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      ) : version ? (
        <div className="enrollment-scenario">
          <div className="enrollment-scenario__editor">
            <ScenarioEditorWrapper
              key={previewActive ? `preview-${version.id}` : version.id}
              id={version.id}
              scenarioId={scenario.id}
              scenario={previewActive ? previewGraph : version.data}
              scenarioTitle="Subject Invitation"
              update={previewActive ? undefined : handleEditorChange}
              isPreview={previewActive}
              tasks={[]}
              fetchers={fetchers}
              itemType="enrollment"
            />
          </div>

          <ChangeLogsModalComponent
            isModalOpen={logsOpen}
            handleCancel={() => setLogsOpen(false)}
            title="Changes"
            logs={changeLogs}
            onPreviewActive={handlePreviewActive}
          />
        </div>
      ) : (
        <p>Failed to load.</p>
      )}
    </Body>
  );
};

export default EnrollmentScenario;
